import { allFaqs, generalFaq, ehrFaq, shopFaq } from "utils/data/faqData";

/**
 * Get FAQ sections by category names
 * @param {Array<string>} categoryNames - Array of category names to retrieve
 * @param {string} faqSource - Source of FAQs ('all', 'shop', 'general', 'ehr')
 * @returns {Array} - Array of FAQ sections matching the category names
 */
export const getFaqsByCategories = (categoryNames = [], faqSource = "all") => {
  if (!categoryNames || categoryNames.length === 0) {
    return [];
  }

  let faqData;
  switch (faqSource.toLowerCase()) {
    case "shop":
      faqData = shopFaq;
      break;
    case "general":
      faqData = generalFaq;
      break;
    case "ehr" || "ehr-implementation":
      faqData = ehrFaq;
      break;
    default:
      faqData = allFaqs;
  }

  return faqData.filter((section) => categoryNames.includes(section.category));
};

/**
 * Extract all FAQ data from page content configuration
 * This handles both static faqSections and dynamic faqCategories
 * @param {Array} contentData - Page content data array
 * @returns {Array} - Flattened array of FAQ sections ready for filtering
 */
export const extractFaqDataFromContent = (contentData = []) => {
  if (!contentData || contentData.length === 0) {
    return [];
  }

  const allFaqSections = [];

  contentData.forEach((item) => {
    const section = item?.section;

    if (!section) return;

    // Helper to process FAQ content
    const processFaqContent = (content) => {
      if (!content) return;

      // Case 1: Static faqSections already exist
      if (content.faqSections && Array.isArray(content.faqSections)) {
        allFaqSections.push(...content.faqSections);
      }
      // Case 2: Dynamic faqCategories need to be resolved
      else if (content.faqCategories && Array.isArray(content.faqCategories)) {
        const faqSource = content.faqSource || "all";
        const dynamicFaqSections = getFaqsByCategories(content.faqCategories, faqSource);
        allFaqSections.push(...dynamicFaqSections);
      }
    };

    // Check if section itself is a FAQ component
    if (section.componentType === "Faq" && section.content) {
      processFaqContent(section.content);
    }

    // Check TwoColumnSection for FAQ in either column
    if (section.componentType === "TwoColumnSection") {
      // Check right column
      if (section.rightColumn?.componentType === "Faq" && section.rightColumn?.content) {
        processFaqContent(section.rightColumn.content);
      }

      // Check left column
      if (section.leftColumn?.componentType === "Faq" && section.leftColumn?.content) {
        processFaqContent(section.leftColumn.content);
      }

      // Handle array of items in columns
      if (Array.isArray(section.rightColumn?.items)) {
        section.rightColumn.items.forEach((item) => {
          if (item.componentType === "Faq" && item.content) {
            processFaqContent(item.content);
          }
        });
      }

      if (Array.isArray(section.leftColumn?.items)) {
        section.leftColumn.items.forEach((item) => {
          if (item.componentType === "Faq" && item.content) {
            processFaqContent(item.content);
          }
        });
      }
    }
  });

  return allFaqSections;
};
